import { useId } from 'react';
import type { DrawingBox, HandsetDrawing, HandsetLayout } from '@arcnet/contracts';

/**
 * The phone's hardware under its keys (owner, 31 October 2026: "pretty much a photo realistic
 * copy"): the deck, the cradle, the handset, the screen housing and the trim, drawn as SVG
 * paths from the same measured boxes HandsetFace places its keys on. The boxes say where each
 * part is; the family (its finish) says what shape and plastic it is: a T4's wedge deck and
 * brushed foot, a T5's cradle column and tilted screen, Fanvil's glossy black with a chamfered
 * foot and its red lamp, a cordless handset's rounded body and glass. The colours are CSS
 * tokens (tokens.css, app.css by finish) so the drawing follows one styling system and both
 * themes. No maker's name or mark is drawn.
 */

/** The family a model is drawn as: its plastic, its shapes and its key colours. */
export type Finish = 'yealink-t3' | 'yealink-t4' | 'yealink-t5' | 'yealink-t7' | 'yealink-dect' | 'fanvil-x' | 'fanvil-dect';

/**
 * From the model name: Yealink's desk phones are T3x, T4x, T5x and the T7x/T8x (one "Classic
 * Grey" family, 31 October 2026), its handsets W7xH and AX8xH; Fanvil's desk phones are
 * X-series, its handsets W6xx and W7xx (three digits).
 */
export function finishOf(model: string, face: HandsetLayout['face']): Finish {
  const m = model.trim().toUpperCase();
  if (face === 'cordless') return /^W\d{3}/.test(m) ? 'fanvil-dect' : 'yealink-dect';
  if (/^X/.test(m)) return 'fanvil-x';
  if (/^T3/.test(m)) return 'yealink-t3';
  if (/^T5/.test(m)) return 'yealink-t5';
  if (/^T[78]/.test(m)) return 'yealink-t7';
  return 'yealink-t4';
}

type Pt = readonly [number, number];
const f = ([x, y]: Pt) => `${x.toFixed(1)},${y.toFixed(1)}`;

/** A closed path through the corners, each rounded by its radius, or by as much as its sides allow. */
function rounded(pts: Pt[], radii: number | number[]): string {
  const n = pts.length;
  const r = (i: number) => (Array.isArray(radii) ? radii[i] ?? 0 : radii);
  let d = '';
  for (let i = 0; i < n; i++) {
    const [px, py] = pts[(i + n - 1) % n]!;
    const [cx, cy] = pts[i]!;
    const [nx, ny] = pts[(i + 1) % n]!;
    const inL = Math.hypot(cx - px, cy - py);
    const outL = Math.hypot(nx - cx, ny - cy);
    const rr = Math.min(r(i), inL / 2, outL / 2);
    const a: Pt = [cx + ((px - cx) / inL) * rr, cy + ((py - cy) / inL) * rr];
    const b: Pt = [cx + ((nx - cx) / outL) * rr, cy + ((ny - cy) / outL) * rr];
    d += `${i === 0 ? 'M' : 'L'}${f(a)} Q${f([cx, cy])} ${f(b)} `;
  }
  return `${d}Z`;
}

const rect = ([x, y, w, h]: DrawingBox, radii: number | number[]) =>
  rounded([[x, y], [x + w, y], [x + w, y + h], [x, y + h]], radii);

/** What a body box is, by its shape: the cradle column beside the handset, the small stand under a tilted screen, or the deck. */
const roleOf = ([, , w, h]: DrawingBox): 'column' | 'stand' | 'deck' =>
  h > 1.4 * w ? 'column' : h < 40 && w < 320 ? 'stand' : 'deck';

/** A polygon as its corners and their radii, clockwise from the top left. */
interface Poly { pts: Pt[]; radii: number[] }

/** The deck as the family shapes it. */
function deckPoly([x, y, w, h]: DrawingBox, finish: Finish): Poly {
  if (finish === 'fanvil-x') {
    // Fanvil's deck: square shoulders, and a foot that comes forward to a shallow point.
    const dip = h * 0.07;
    return {
      pts: [
        [x + 6, y], [x + w - 6, y], [x + w, y + 8], [x + w, y + h - dip],
        [x + w * 0.6, y + h], [x + w * 0.4, y + h], [x, y + h - dip], [x, y + 8],
      ],
      radii: [8, 8, 6, 6, 26, 26, 6, 6],
    };
  }
  // Yealink's deck: a wedge the photo sees a little narrower at the top, rounded at the foot.
  return { pts: [[x + 5, y], [x + w - 5, y], [x + w, y + h], [x, y + h]], radii: [14, 14, 22, 22] };
}

/**
 * The deck with the cradle column that rises from its left to hold the handset, as one
 * outline: the column's top corners replace the deck's top left corner, so there is no seam
 * where the photo shows none.
 */
function deckWithColumn(deck: DrawingBox, column: DrawingBox, finish: Finish): Poly {
  const { pts, radii } = deckPoly(deck, finish);
  const [cx, cy, cw] = column;
  const r = finish === 'fanvil-x' ? 10 : 22;
  const top: Pt[] = [[cx, cy], [cx + cw, cy], [cx + cw, pts[0]![1]]];
  const left: Pt[] = [[cx, pts[pts.length - 1]![1]]];
  return { pts: [...top, ...pts.slice(1, -1), ...left], radii: [r, r, 4, ...radii.slice(1, -1), 4] };
}

/** The top edges of a body outline, where its chamfer catches the light: each level run along the top. */
function litEdges({ pts }: Poly): string {
  const top = Math.min(...pts.map((p) => p[1]));
  const runs: string[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const [a, b] = [pts[i]!, pts[i + 1]!];
    if (a[1] === b[1] && a[1] <= top + 160 && b[0] > a[0]) runs.push(`M${f([a[0] + 10, a[1] + 1.5])} L${f([b[0] - 10, b[1] + 1.5])}`);
  }
  return runs.join(' ');
}

/** The body of a desk phone: its deck, the column beside the handset, and a T5's stand under the screen. */
function deskBody(d: HandsetDrawing, finish: Finish): { d: string; className: string }[] {
  const roles = d.body.map(roleOf);
  const deck = d.body[roles.indexOf('deck')] ?? d.body[0]!;
  const column = d.body[roles.indexOf('column')];
  // A T3 or T4 measures no column, but its deck still rises behind the handset's lower part.
  const restTop = d.handset ? d.handset[1] + d.handset[3] * 0.2 : Infinity;
  const rest: DrawingBox | undefined = column ?? (d.handset && finish !== 'fanvil-x' && restTop < deck[1] - 10
    ? [d.handset[0] - 14, restTop, d.handset[2] + 28, deck[1] - restTop]
    : undefined);
  const outline = rest ? deckWithColumn(deck, rest, finish) : deckPoly(deck, finish);
  const parts = [{ d: rounded(outline.pts, outline.radii), className: 'arc-shell-edge' }];
  for (const [i, role] of roles.entries()) {
    if (role === 'stand') parts.push({ d: rect(d.body[i]!, 6), className: 'arc-shell-edge arc-shell-stand' });
  }
  parts.push({ d: litEdges(outline), className: 'arc-shell-light' });
  return parts;
}

/** The chamfered face along Fanvil's foot, where it catches the light. */
function facetPath([x, y, w, h]: DrawingBox): string {
  const dip = h * 0.07;
  const lift = h * 0.03;
  return rounded([
    [x, y + h - dip - lift], [x + w * 0.4, y + h - lift], [x + w * 0.6, y + h - lift], [x + w, y + h - dip - lift],
    [x + w, y + h - dip], [x + w * 0.6, y + h], [x + w * 0.4, y + h], [x, y + h - dip],
  ], 4);
}

/**
 * The handset seen from behind in its cradle: a wide earpiece, a neck into a narrower grip,
 * and a mouthpiece that widens again. `taper` is how much narrower the grip is, a share of
 * the width: Yealink's handsets wasp more than Fanvil's straighter ones.
 */
function handsetPath([x, y, w, h]: DrawingBox, taper: number): string {
  const l = x;
  const r = x + w;
  const cx = x + w / 2;
  const t = w * taper;
  const neck = y + h * 0.22;
  const waist = y + h * 0.34;
  const grip = y + h * 0.76;
  const mouth = y + h * 0.9;
  const m = w * 0.03;
  return [
    `M${f([cx, y])}`,
    `C${f([r - w * 0.14, y])} ${f([r, y + h * 0.03])} ${f([r, y + h * 0.1])}`,
    `L${f([r, neck])}`,
    `C${f([r, neck + h * 0.06])} ${f([r - t, waist - h * 0.02])} ${f([r - t, waist])}`,
    `L${f([r - t, grip])}`,
    `C${f([r - t, grip + h * 0.06])} ${f([r - m, mouth - h * 0.05])} ${f([r - m, mouth])}`,
    `C${f([r - m, y + h - h * 0.02])} ${f([r - w * 0.2, y + h])} ${f([cx, y + h])}`,
    `C${f([l + w * 0.2, y + h])} ${f([l + m, y + h - h * 0.02])} ${f([l + m, mouth])}`,
    `C${f([l + m, mouth - h * 0.05])} ${f([l + t, grip + h * 0.06])} ${f([l + t, grip])}`,
    `L${f([l + t, waist])}`,
    `C${f([l + t, waist - h * 0.02])} ${f([l, neck + h * 0.06])} ${f([l, neck])}`,
    `L${f([l, y + h * 0.1])}`,
    `C${f([l, y + h * 0.03])} ${f([l + w * 0.14, y])} ${f([cx, y])}Z`,
  ].join(' ');
}

export function HandsetShell({ d, finish }: { d: HandsetDrawing; finish: Finish }) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, '');
  const u = (name: string) => `url(#${id}-${name})`;
  const cordless = !d.handset;
  const fanvil = finish === 'fanvil-x';
  const deckTop = Math.min(...d.body.map((b) => b[1]));
  const decks = d.body.filter((b) => roleOf(b) === 'deck');

  return (
    <svg className="arc-phone-shell" viewBox={`0 0 1000 ${d.height}`} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`${id}-body`} x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0" className="arc-shell-body-hi" />
          <stop offset="0.45" className="arc-shell-body" />
          <stop offset="1" className="arc-shell-body-lo" />
        </linearGradient>
        <linearGradient id={`${id}-flank`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" className="arc-shell-rim" />
          <stop offset="0.16" className="arc-shell-body-hi" />
          <stop offset="0.5" className="arc-shell-body" />
          <stop offset="0.86" className="arc-shell-body-lo" />
          <stop offset="1" className="arc-shell-rim" />
        </linearGradient>
        <linearGradient id={`${id}-hand`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" className="arc-shell-rim" />
          <stop offset="0.24" className="arc-shell-handset-hi" />
          <stop offset="0.5" className="arc-shell-handset" />
          <stop offset="0.84" className="arc-shell-handset-lo" />
          <stop offset="1" className="arc-shell-rim" />
        </linearGradient>
        <linearGradient id={`${id}-cap`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity=".14" />
          <stop offset="0.07" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.24" stopColor="#000" stopOpacity="0" />
          <stop offset="0.31" stopColor="#000" stopOpacity=".16" />
          <stop offset="0.38" stopColor="#000" stopOpacity="0" />
          <stop offset="0.84" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity=".4" />
        </linearGradient>
        <linearGradient id={`${id}-pod`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="arc-shell-pod-hi" />
          <stop offset="0.3" className="arc-shell-pod" />
          <stop offset="1" className="arc-shell-pod-lo" />
        </linearGradient>
        <linearGradient id={`${id}-sheen`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity=".11" />
          <stop offset="1" stopColor="#fff" stopOpacity=".02" />
        </linearGradient>
        <linearGradient id={`${id}-trim`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="arc-shell-trim-hi" />
          <stop offset="0.5" className="arc-shell-trim" />
          <stop offset="1" className="arc-shell-trim-lo" />
        </linearGradient>
        <pattern id={`${id}-brush`} width="3" height="1" patternUnits="userSpaceOnUse">
          <rect width="1" height="1" fill="#fff" fillOpacity=".12" />
        </pattern>
        <filter id={`${id}-lift`} x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx="5" dy="8" stdDeviation="6" floodColor="#000" floodOpacity=".5" />
        </filter>
        <filter id={`${id}-set`} x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx="0" dy="5" stdDeviation="5" floodColor="#000" floodOpacity=".45" />
        </filter>
        {d.pod && (
          <clipPath id={`${id}-glass`}>
            <path d={rect(d.pod, cordless ? d.pod[2] * 0.07 : fanvil ? 10 : 14)} />
          </clipPath>
        )}
      </defs>

      {/* The body: a cordless handset's rounded flanks, or a desk phone's deck, column and stand. */}
      {cordless ? (
        <path d={rect(d.body[0]!, finish === 'fanvil-dect' ? [120, 120, 160, 160] : [200, 200, 240, 240])}
          fill={u('flank')} className="arc-shell-edge" />
      ) : deskBody(d, finish).map((part, i) => (
        <path key={i} d={part.d} fill={u('body')} className={part.className} />
      ))}
      {!cordless && fanvil && decks.map((b, i) => (
        <path key={`facet${i}`} d={facetPath(b)} className="arc-shell-facet" />
      ))}
      {!cordless && d.trim && (
        <g>
          <path d={rect(d.trim, [0, 0, 14, 14])} fill={u('trim')} />
          <path d={rect(d.trim, [0, 0, 14, 14])} fill={u('brush')} />
          <path d={rect(d.trim, [0, 0, 14, 14])} className="arc-shell-trim-edge" />
        </g>
      )}

      {/* The cradle's recess, and the handset lying in it. */}
      {d.handset && (() => {
        const [x, y, w, h] = d.handset;
        const top = Math.max(y + h * 0.2, deckTop + 6);
        const taper = fanvil ? 0.045 : 0.085;
        return (
          <g>
            <path d={rect([x - 6, top, w + 12, y + h + 10 - top], 18)} className="arc-shell-recess" />
            <path d={handsetPath(d.handset, taper)} fill={u('hand')} filter={u('lift')} className="arc-shell-edge" />
            <path d={handsetPath(d.handset, taper)} fill={u('cap')} />
            <text x={x + w / 2} y={y + h * 0.47} className="arc-shell-emboss" fontSize={w * 0.16} textAnchor="middle">HD</text>
          </g>
        );
      })()}

      {/* The screen housing: a desk phone's dark tilted panel with its lamp, or a cordless handset's glass. */}
      {d.pod && (() => {
        const [x, y, w, h] = d.pod;
        const r = cordless ? w * 0.07 : fanvil ? 10 : 14;
        return (
          <g>
            <path d={rect(d.pod, r)} fill={u('pod')} filter={cordless ? undefined : u('set')} className="arc-shell-edge" />
            <path d={`M${x},${y} L${x + w * 0.58},${y} L${x + w * 0.22},${y + h} L${x},${y + h}Z`}
              fill={u('sheen')} clipPath={u('glass')} />
            <path d={rect([x + 1.5, y + 1.5, w - 3, h - 3], r)} className="arc-shell-inner" />
            {!cordless && !fanvil && <path d={rect([x + 4, y + h - 9, w - 8, 9], [0, 0, 10, 10])} className="arc-shell-lip" />}
            {!cordless && (fanvil
              ? <rect x={x + w - 12} y={y + 7} width="5" height="20" rx="2.5" className="arc-shell-lamp" />
              : <circle cx={x + w - 17} cy={y + 13} r="4.5" className="arc-shell-lamp" />)}
            {cordless && (
              <>
                <rect x={x + w * 0.42} y={y + 11} width={w * 0.16} height="7" rx="3.5" className="arc-shell-slot" />
                <circle cx={d.body[0]![0] + d.body[0]![2] / 2} cy={d.body[0]![1] + d.body[0]![3] - 22} r="3.5" className="arc-shell-slot" />
              </>
            )}
          </g>
        );
      })()}
    </svg>
  );
}
